import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getFeeConfig, updateFeeConfig } from '@/lib/api';
import { DollarSign, Save, Loader2 } from 'lucide-react';

export const FeeConfigTab = () => {
  const { t } = useTranslation();
  const [config, setConfig] = useState({ fee_type: 'none', fixed_amount_eur: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConfig = async () => {
    try {
      const res = await getFeeConfig();
      setConfig(res.data);
    } catch {
      toast.error(t('admin.fee.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateFeeConfig(config);
      toast.success(t('admin.fee.savedSuccess'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('admin.common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-muted-foreground">{t('admin.fee.loading')}</div>;

  return (
    <Card data-testid="fee-config-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-emerald-500" />
          {t('admin.fee.title')}
        </CardTitle>
        <CardDescription>
          {t('admin.fee.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>{t('admin.fee.feeType')}</Label>
          <Select
            value={config.fee_type}
            onValueChange={(v) => setConfig(p => ({ ...p, fee_type: v }))}
            data-testid="fee-type-select"
          >
            <SelectTrigger className="w-[300px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">{t('admin.fee.noFee')}</SelectItem>
              <SelectItem value="fixed">{t('admin.fee.fixedFee')}</SelectItem>
              <SelectItem value="percentage">{t('admin.fee.percentageFee')}</SelectItem>
              <SelectItem value="both">{t('admin.fee.both')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {(config.fee_type === 'fixed' || config.fee_type === 'both') && (
          <div className="space-y-2">
            <Label>{t('admin.fee.fixedAmount')}</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={config.fixed_amount_eur || ''}
              onChange={(e) => setConfig(p => ({ ...p, fixed_amount_eur: parseFloat(e.target.value) || 0 }))}
              className="w-[200px]"
              data-testid="fee-fixed-amount-input"
            />
          </div>
        )}

        {(config.fee_type === 'percentage' || config.fee_type === 'both') && (
          <div className="space-y-2">
            <Label>{t('admin.fee.percentage')}</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={config.percentage || ''}
              onChange={(e) => setConfig(p => ({ ...p, percentage: parseFloat(e.target.value) || 0 }))}
              className="w-[200px]"
              data-testid="fee-percentage-input"
            />
          </div>
        )}

        {config.fee_type !== 'none' && (
          <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
            <p className="font-medium mb-1">{t('admin.fee.exampleTitle')}</p>
            <p className="text-muted-foreground">
              {config.fee_type === 'fixed' && `${t('admin.fee.exampleFee')}: ${(config.fixed_amount_eur || 0).toFixed(2)} EUR`}
              {config.fee_type === 'percentage' && `${t('admin.fee.exampleFee')}: ${((config.percentage || 0) / 100 * 1000).toFixed(2)} EUR (${config.percentage}%)`}
              {config.fee_type === 'both' && `${t('admin.fee.exampleFee')}: ${((config.fixed_amount_eur || 0) + (config.percentage || 0) / 100 * 1000).toFixed(2)} EUR (${(config.fixed_amount_eur || 0).toFixed(2)} EUR + ${config.percentage}%)`}
            </p>
          </div>
        )}

        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600" data-testid="save-fee-config-btn">
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          {t('common.save')}
        </Button>
      </CardContent>
    </Card>
  );
};
